// In-Location movement kinematics (design doc Section 2a's "Move To"
// pursuit model), ported from the tested mockup
// (mockups/starforge_combat_mockup_v2.html's stepMover/updateOrbitPoint) --
// literal port of the tuning constants (turn rate, accel/decel curves),
// not a re-derivation. Pure/deterministic given (state, dt, target) so
// it's unit-testable independent of any render loop or wall clock.

export interface Kinematics {
  x: number;
  y: number;
  heading: number; // radians
  speed: number; // m/s
}

export interface Point {
  x: number;
  y: number;
}

// Turn rate ~200deg/s -- matches the locked Fast Travel turn rate
// (ui/starforge-ui-tokens.css's --sf-flee-turn-rate-deg-per-s), reused
// here since Section 2a doesn't specify a separate in-Location turn rate.
const TURN_RATE_RAD_PER_S = Math.PI * 1.1;

function normalizeAngleDiff(diff: number): number {
  let d = diff;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}

// Steers `mover` one time-slice toward `target`: turns at a fixed rate,
// accelerates toward top speed while beyond `stopDistance`, decelerates to
// a stop once within it. A null target decelerates the mover to a stop
// without changing heading (mirrors the mockup's "no target" branch).
export function stepTowardTarget(
  mover: Kinematics,
  dt: number,
  stopDistance: number,
  topSpeed: number,
  target: Point | null
): Kinematics {
  if (!target) {
    return { ...mover, speed: Math.max(0, mover.speed - (topSpeed / 1.2) * dt) };
  }

  const dx = target.x - mover.x;
  const dy = target.y - mover.y;
  const distance = Math.hypot(dx, dy);
  const desiredHeading = Math.atan2(dy, dx);
  const maxTurn = TURN_RATE_RAD_PER_S * dt;
  const heading = mover.heading + Math.max(-maxTurn, Math.min(maxTurn, normalizeAngleDiff(desiredHeading - mover.heading)));

  const desiredSpeed = distance > stopDistance ? topSpeed : 0;
  const accel = topSpeed / 1.4;
  const speed =
    mover.speed < desiredSpeed
      ? Math.min(desiredSpeed, mover.speed + accel * dt)
      : Math.max(desiredSpeed, mover.speed - accel * dt);

  return {
    x: mover.x + Math.cos(heading) * speed * dt,
    y: mover.y + Math.sin(heading) * speed * dt,
    heading,
    speed
  };
}

export interface OrbitPoint {
  angle: number; // radians
  radius: number; // meters
}

// Advances the moving point an orbiting ship chases (Orbit Near/Far,
// Section 4b) -- angular speed scales with the orbiter's own top speed so
// a faster ship doesn't lag a slower orbit radius.
export function stepOrbitPoint(orbit: OrbitPoint, dt: number, orbiterTopSpeed: number): OrbitPoint {
  const angularSpeed = Math.max(0.15, Math.min(1.0, orbiterTopSpeed / orbit.radius));
  return { ...orbit, angle: orbit.angle + angularSpeed * dt };
}

export function orbitPointPosition(center: Point, orbit: OrbitPoint): Point {
  return {
    x: center.x + Math.cos(orbit.angle) * orbit.radius,
    y: center.y + Math.sin(orbit.angle) * orbit.radius
  };
}

// ================= Fast Travel sequence (Section 2a/4b, LOCKED) =================
// turning -> accelerating -> exiting, ported from the mockup's stepFlee.
// Unlike stepTowardTarget (seek-and-stop), this always ends up moving in a
// straight line at top speed -- there is no destination point in-world,
// the ship just needs to clear the vulnerable turn/accelerate window.

export type FleeStage = "turning" | "accelerating" | "exiting";

// 0 -> top speed in ~1.5s (--sf-flee-accel-seconds), distinct from
// stepTowardTarget's ordinary accel constant -- ported as its own value
// since the mockup's stepFlee uses a different curve for this stage.
const FLEE_ACCEL_SECONDS = 1.5;

export interface FleeStepResult {
  ship: Kinematics;
  stage: FleeStage;
  // True on the exact tick top speed is reached -- the signal to unlock
  // the camera and let the ship visibly fly out of frame (Section 4b).
  justReachedTopSpeed: boolean;
}

// One time-slice of the Fast Travel sequence. `targetHeading` is the
// randomized heading rolled once at the start of the whole sequence (not
// re-rolled per tick).
export function stepFlee(ship: Kinematics, dt: number, stage: FleeStage, targetHeading: number, topSpeed: number): FleeStepResult {
  if (stage === "turning") {
    const diff = normalizeAngleDiff(targetHeading - ship.heading);
    const maxTurn = TURN_RATE_RAD_PER_S * dt;
    const heading = ship.heading + Math.max(-maxTurn, Math.min(maxTurn, diff));
    // The ship must decelerate to a full stop as part of turning,
    // regardless of prior speed (e.g. still orbiting at top speed) -- a
    // real bug caught during mockup testing (see design doc Section 4b).
    const decel = topSpeed / 1.0;
    const speed = Math.max(0, ship.speed - decel * dt);
    const next: Kinematics = {
      x: ship.x + Math.cos(heading) * speed * dt,
      y: ship.y + Math.sin(heading) * speed * dt,
      heading,
      speed
    };
    const doneTurning = Math.abs(diff) < 0.03 && speed <= 0.5;
    return { ship: next, stage: doneTurning ? "accelerating" : "turning", justReachedTopSpeed: false };
  }

  if (stage === "accelerating") {
    const accel = topSpeed / FLEE_ACCEL_SECONDS;
    const speed = Math.min(topSpeed, ship.speed + accel * dt);
    const next: Kinematics = {
      x: ship.x + Math.cos(ship.heading) * speed * dt,
      y: ship.y + Math.sin(ship.heading) * speed * dt,
      heading: ship.heading,
      speed
    };
    const reachedTopSpeed = speed >= topSpeed - 1;
    return { ship: next, stage: reachedTopSpeed ? "exiting" : "accelerating", justReachedTopSpeed: reachedTopSpeed };
  }

  // exiting -- straight line at top speed, camera already unlocked
  const next: Kinematics = {
    x: ship.x + Math.cos(ship.heading) * topSpeed * dt,
    y: ship.y + Math.sin(ship.heading) * topSpeed * dt,
    heading: ship.heading,
    speed: topSpeed
  };
  return { ship: next, stage: "exiting", justReachedTopSpeed: false };
}

// ================= Cruise (Engine tapped with no target) =================
// Accelerates to top speed and continues in a straight line along
// whatever heading the ship already had the moment it was engaged --
// heading never changes (this is not a seek/pursuit, there is no
// destination), unlike stepTowardTarget which turns to face a target.
export function stepCruise(mover: Kinematics, dt: number, topSpeed: number): Kinematics {
  const accel = topSpeed / 1.4; // matches stepTowardTarget's ordinary accel curve
  const speed = Math.min(topSpeed, mover.speed + accel * dt);
  return {
    x: mover.x + Math.cos(mover.heading) * speed * dt,
    y: mover.y + Math.sin(mover.heading) * speed * dt,
    heading: mover.heading,
    speed
  };
}

// ================= Brake (Engine tapped while already moving) =================
// Decelerates to a stop in the current heading at the exact same rate
// stepCruise/stepTowardTarget accelerate at (topSpeed/1.4) -- symmetric
// with ramping up, per Don's direction. Distinct from stepTowardTarget's
// own null-target branch, which intentionally uses a gentler passive-coast
// rate for "nothing is engaged right now" rather than a deliberate brake.
export function stepBrake(mover: Kinematics, dt: number, topSpeed: number): Kinematics {
  const decel = topSpeed / 1.4;
  const speed = Math.max(0, mover.speed - decel * dt);
  return {
    x: mover.x + Math.cos(mover.heading) * speed * dt,
    y: mover.y + Math.sin(mover.heading) * speed * dt,
    heading: mover.heading,
    speed
  };
}
